import { cn } from '@/lib/utils';
import type { AmenityCategoryMeta } from '@/lib/map-constants';

export interface CategoryToggleProps {
  meta: AmenityCategoryMeta;
  active: boolean;
  onToggle: () => void;
}

/**
 * `<CategoryToggle>` — single chip toggling visibility of one amenity
 * category on `/app/map`. Used by both `<MapFilterBar>` (chips row) and
 * implicitly by the legend's clickable rows.
 *
 * UI-SPEC § 4.3:
 * - Active   = colored swatch + foam-50 text on tinted (~20% alpha) bg
 * - Inactive = muted swatch + foam-50/50 text + line-through label
 *
 * Tailwind cannot interpolate dynamic colors at compile time, so the
 * tinted background and border colors are applied via inline `style`.
 * The Glass Coast palette is fixed to 4 hex values (per
 * AMENITY_CATEGORIES); the linter has nothing to flag because we never
 * construct class names dynamically — only inline-style values.
 *
 * Accessibility: `role="switch"` + `aria-checked` describe the on/off
 * state. The full label ("Toggle {Dog Parks} amenities") is in
 * `aria-label`; visible text uses the short label form.
 */
export function CategoryToggle({ meta, active, onToggle }: CategoryToggleProps) {
  // Short visible labels per UI-SPEC § 4.3 ("Parks · Beaches · Daycare · Bars").
  // Legend rows use the full meta.label.
  const shortLabel = meta.label
    .replace('Dog Parks', 'Parks')
    .replace('Dog Beaches', 'Beaches')
    .replace('Dog-Friendly Bars', 'Bars');

  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label={`Toggle ${meta.label} amenities`}
      onClick={onToggle}
      className={cn(
        'inline-flex h-8 px-3 rounded-full border items-center gap-1.5 text-sm font-semibold cursor-pointer',
        'transition-colors',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        active
          ? 'border-foam-50/40 text-foam-50'
          : 'border-foam-50/10 text-foam-50/50 line-through bg-ocean-800/40',
      )}
      style={
        active
          ? {
              // hex + alpha-suffix shorthand: 33 ≈ 20% / 99 ≈ 60%
              backgroundColor: `${meta.color}33`,
              borderColor: `${meta.color}99`,
            }
          : undefined
      }
    >
      <span
        aria-hidden
        className="w-3 h-3 rounded-full shrink-0"
        style={{ backgroundColor: meta.color }}
      />
      {shortLabel}
    </button>
  );
}
